import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  downloadProductLedgerPdf,
  fetchProductMoneyFlowReport,
  type ProductMoneyFlowHeroCard,
  type ProductMoneyFlowReport,
  type ProductMoneyFlowTransaction,
} from "@/lib/catalog/productLedgerStore";
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  Download,
  FileText,
  Loader2,
  Search,
  User,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

export type ProductLedgerDialogProps = {
  open: boolean;
  productId: string | number | null;
  onOpenChange: (open: boolean) => void;
};

function money(amount: number): string {
  return Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function displayStatusForRow(
  row: ProductMoneyFlowTransaction,
): "Settled" | "Additional Due" | "Excess" | "Partially Paid" | "Pending" {
  const status = String(row.status ?? "").toLowerCase();
  const label = String(row.statusLabel ?? "").toLowerCase();
  const excess = Number(row.excessAmount ?? 0);
  const due = Number(row.dueAmount ?? Math.max(0, (row.requiredAmount ?? row.amount) - (row.settledAmount ?? 0)));
  const settledAmount = Number(row.settledAmount ?? (status === "settled" ? row.amount : 0));

  if (status === "excess" || label === "excess" || excess > 0.01) return "Excess";
  if (label === "additional due" || (due > 0.01 && settledAmount > 0.01)) return "Additional Due";
  if (due <= 0.01 && excess <= 0.01 && (status === "settled" || settledAmount >= (row.requiredAmount ?? row.amount) - 0.01)) {
    return "Settled";
  }
  if (settledAmount > 0.01) return "Partially Paid";
  return "Pending";
}

function HeroCard({
  card,
  currencySymbol,
  showVendorTooltip,
}: {
  card: ProductMoneyFlowHeroCard;
  currencySymbol: string;
  showVendorTooltip?: boolean;
}) {
  const isGreen = card.color !== "red";
  const settledLabel = card.settledLabel || (isGreen ? "Received" : "Paid Out");
  const vendors = card.vendors ?? [];
  const vendorCount = card.vendorCount ?? vendors.length;
  const partnerCount = card.partnerCount ?? 0;
  const moreVendors = Math.max(0, vendorCount - 1);
  const morePartners = Math.max(0, partnerCount - 1);

  return (
    <div className="rounded-2xl border border-border/70 bg-card/60 p-5 shadow-xs flex flex-col justify-between space-y-4">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5 min-w-0">
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  isGreen ? "bg-emerald-500" : "bg-rose-500",
                  isGreen && card.title.toLowerCase().includes("customer") && "animate-pulse",
                )}
              />
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {card.title}
              </span>
            </div>

            {showVendorTooltip && vendors.length > 0 ? (
              <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap mt-0.5">
                <span>{card.partyLabel || "Payable to"}</span>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-semibold text-foreground hover:text-primary transition-colors cursor-pointer text-left"
                    >
                      {card.partyName || vendors[0]?.name}
                      {moreVendors > 0 && (
                        <span className="rounded bg-muted px-1.5 py-0.2 text-[10px] font-bold text-muted-foreground">
                          +{moreVendors} more ({vendorCount} vendors)
                        </span>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    className="text-xs p-3 space-y-2 max-w-sm bg-popover text-popover-foreground border shadow-xl"
                  >
                    <p className="font-bold border-b border-border/40 pb-1 text-[11px] uppercase tracking-wider text-foreground">
                      Vehicle Suppliers & Service Providers
                    </p>
                    <div className="space-y-1.5">
                      {vendors.map((vendor) => (
                        <div key={vendor.name} className="flex items-center justify-between gap-4 text-[11px]">
                          <span className="font-semibold text-foreground">{vendor.name}</span>
                          <span className="font-mono font-medium text-foreground tabular-nums">
                            {currencySymbol} {money(vendor.totalAmount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap mt-0.5">
                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{card.partyLabel}:</span>
                <strong className="text-foreground">{card.partyName}</strong>
                {card.partyPhone ? (
                  <span className="text-[11px] text-muted-foreground">({card.partyPhone})</span>
                ) : null}
                {morePartners > 0 ? (
                  <span className="rounded bg-muted px-1.5 py-0.2 text-[10px] font-bold text-muted-foreground">
                    +{morePartners} more partner(s)
                  </span>
                ) : null}
              </div>
            )}
          </div>
          <div
            className={cn(
              "h-7 w-7 rounded-lg flex items-center justify-center shrink-0 border",
              isGreen
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
            )}
          >
            {isGreen ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
          </div>
        </div>

        <div className="mt-3">
          <p
            className={cn(
              "text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums",
              isGreen
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400",
            )}
          >
            {currencySymbol} {money(card.amount)}
          </p>
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {settledLabel}:{" "}
              <strong
                className={cn(
                  "font-semibold tabular-nums",
                  isGreen
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-rose-600 dark:text-rose-400",
                )}
              >
                {currencySymbol} {money(card.settledAmount)}
              </strong>
            </span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold text-foreground">
              {card.settledPct.toFixed(1)}
              {card.pctLabel || "%"}
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted/60 overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-500",
                isGreen ? "bg-emerald-500" : "bg-rose-500",
              )}
              style={{ width: `${Math.min(100, Math.max(card.settledPct, 0))}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs pt-0.5">
            <span className="text-muted-foreground">
              {isGreen ? "Remains Outstanding:" : "Remains Payable:"}
            </span>
            <span className="font-bold text-foreground tabular-nums">
              {currencySymbol} {money(card.outstandingAmount)}
            </span>
          </div>
          {(card.excessAmount ?? 0) > 0.01 ? (
            <div className="flex items-center justify-between text-xs pt-0.5">
              <span className="text-muted-foreground">Excess:</span>
              <span className="font-bold text-amber-700 dark:text-amber-400 tabular-nums">
                {currencySymbol} {money(card.excessAmount ?? 0)}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="border-t border-border/50 pt-2.5 flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          {isGreen ? (
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          )}
          {card.footerLeft}
        </span>
        <span
          className={cn(
            "font-semibold",
            isGreen
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-rose-600 dark:text-rose-400",
          )}
        >
          {card.footerRight}
        </span>
      </div>
    </div>
  );
}

export function ProductLedgerDialog({ open, productId, onOpenChange }: ProductLedgerDialogProps) {
  const [report, setReport] = useState<ProductMoneyFlowReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [flowFilter, setFlowFilter] = useState<"all" | "receivables" | "payables">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "settled">("all");
  const [partyFilter, setPartyFilter] = useState<string>("all");
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  useEffect(() => {
    if (!open || productId == null || productId === "") {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    setSearch("");
    setFlowFilter("all");
    setStatusFilter("all");
    setPartyFilter("all");
    setExpandedIds({});
    setAllExpanded(false);

    fetchProductMoneyFlowReport(productId)
      .then((data) => {
        if (!cancelled) {
          setReport(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load product money flow report.");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, productId]);

  const currencySymbol = report?.currency?.trim() || "Rs.";
  const isSold = Boolean(report?.product.isSold);
  const transactions = report?.transactions ?? [];

  const uniqueParties = useMemo(() => {
    return Array.from(new Set(transactions.map((row) => row.party).filter(Boolean)));
  }, [transactions]);

  const filteredRows = useMemo(() => {
    return transactions.filter((row) => {
      if (flowFilter === "receivables" && row.flow !== "receivable") return false;
      if (flowFilter === "payables" && row.flow !== "payable") return false;

      const displayStatus = displayStatusForRow(row);
      if (statusFilter === "settled" && displayStatus !== "Settled") return false;
      if (statusFilter === "pending" && displayStatus === "Settled") return false;

      if (partyFilter !== "all" && row.party !== partyFilter) return false;

      const q = search.trim().toLowerCase();
      if (q) {
        const haystack = [
          row.title,
          row.description,
          row.party,
          row.partyType,
          row.refCode,
          row.category,
          row.settlementText,
          String(row.amount),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [transactions, flowFilter, statusFilter, partyFilter, search]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleExpandAll = () => {
    const nextState = !allExpanded;
    setAllExpanded(nextState);
    const next: Record<string, boolean> = {};
    if (nextState) {
      filteredRows.forEach((row) => {
        if (row.breakdownItems?.length) next[row.id] = true;
      });
    }
    setExpandedIds(next);
  };

  const handleDownloadPdf = async () => {
    if (productId == null || productId === "") return;
    setPdfDownloading(true);
    try {
      await downloadProductLedgerPdf(productId);
      toast.success("Product ledger PDF downloaded.");
    } catch {
      toast.error("Failed to download PDF.");
    } finally {
      setPdfDownloading(false);
    }
  };

  const profitability = report?.profitability ?? null;
  const netProfit = profitability?.netProfit ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-2rem)] max-w-7xl flex-col overflow-hidden p-0 gap-0 bg-background border border-border/80 rounded-2xl shadow-2xl"
      >
        <DialogHeader className="shrink-0 px-6 sm:px-7 pt-5 pb-4 border-b border-border/50 bg-background/95 backdrop-blur-xs z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl border border-primary/20 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="space-y-0.5 min-w-0">
                <DialogTitle className="text-lg sm:text-xl font-bold font-display tracking-tight text-foreground flex items-center gap-2.5 flex-wrap">
                  <span>
                    Vehicle Financial Ledger
                    {report?.product.name ? ` — ${report.product.name}` : ""}
                  </span>
                  {isSold ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Sold
                    </span>
                  ) : null}
                </DialogTitle>
                {report ? (
                  <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>
                      Ref ID:{" "}
                      <strong className="font-mono text-foreground">{report.product.refId}</strong>
                    </span>
                    {report.product.purchaseDate ? (
                      <>
                        <span>•</span>
                        <span>Purchased: {report.product.purchaseDate}</span>
                      </>
                    ) : null}
                    {isSold && report.product.saleDate ? (
                      <>
                        <span>•</span>
                        <span>
                          Sold: <strong className="text-foreground">{report.product.saleDate}</strong>
                        </span>
                      </>
                    ) : null}
                    {isSold && report.product.saleInvoiceNo ? (
                      <>
                        <span>•</span>
                        <span>
                          Invoice:{" "}
                          <strong className="font-mono text-foreground">
                            #{report.product.saleInvoiceNo}
                          </strong>
                        </span>
                      </>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Product money flow report</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs font-semibold bg-background hover:bg-muted shadow-xs"
                onClick={handleDownloadPdf}
                disabled={pdfDownloading || productId == null}
                title="Download Product Ledger PDF"
              >
                <Download className="h-3.5 w-3.5 text-primary" />
                Export PDF
              </Button>

              <DialogClose asChild>
                <button
                  type="button"
                  className="h-8 w-8 rounded-lg border border-border/70 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                  title="Close dialog (Esc)"
                  aria-label="Close dialog"
                >
                  <X className="h-4 w-4" />
                </button>
              </DialogClose>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 sm:p-7 space-y-5">
          {loading ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium">Loading money flow report…</p>
            </div>
          ) : error ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
              <AlertCircle className="h-8 w-8 text-rose-500" />
              <p className="text-sm font-semibold text-foreground">{error}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (productId == null) return;
                  setLoading(true);
                  setError(null);
                  fetchProductMoneyFlowReport(productId)
                    .then((data) => {
                      setReport(data);
                      setLoading(false);
                    })
                    .catch(() => {
                      setError("Failed to load product money flow report.");
                      setLoading(false);
                    });
                }}
              >
                Retry
              </Button>
            </div>
          ) : report ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <HeroCard card={report.hero.card1} currencySymbol={currencySymbol} />
                <HeroCard
                  card={report.hero.card2}
                  currencySymbol={currencySymbol}
                  showVendorTooltip={!isSold && (report.hero.card2.vendors?.length ?? 0) > 0}
                />
              </div>

              {isSold && profitability ? (
                <div className="rounded-2xl border border-primary/25 bg-primary/[0.03] p-4 shadow-xs space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2.5 border-b border-border/40">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Deal Profitability & Margin Summary
                      </span>
                      <span className="rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-bold">
                        Vehicle Sold
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Net Deal Margin Split between Deal Partners & Showroom
                    </p>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded-xl bg-background/80 border border-border/60 p-3 space-y-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Gross Sale Revenue
                      </p>
                      <p className="text-base sm:text-lg font-extrabold text-foreground tabular-nums font-mono">
                        {currencySymbol} {money(profitability.saleAmount)}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        Invoice #{profitability.saleInvoiceNo || report.product.saleInvoiceNo || "—"}
                      </p>
                    </div>

                    <div className="rounded-xl bg-background/80 border border-border/60 p-3 space-y-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Total Landed Cost
                      </p>
                      <p className="text-base sm:text-lg font-extrabold text-foreground tabular-nums font-mono">
                        {currencySymbol} {money(profitability.vehicleCost)}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        Acquisition + Customs + Charges
                      </p>
                    </div>

                    <div
                      className={cn(
                        "rounded-xl border p-3 space-y-1",
                        netProfit < -0.009
                          ? "bg-rose-500/10 border-rose-500/25"
                          : "bg-emerald-500/10 border-emerald-500/25",
                      )}
                    >
                      <p
                        className={cn(
                          "text-[10px] font-bold uppercase tracking-wider",
                          netProfit < -0.009
                            ? "text-rose-700 dark:text-rose-400"
                            : "text-emerald-700 dark:text-emerald-400",
                        )}
                      >
                        {netProfit < -0.009 ? "Net Deal Loss" : "Net Deal Profit"}
                      </p>
                      <p
                        className={cn(
                          "text-base sm:text-lg font-extrabold tabular-nums font-mono",
                          netProfit < -0.009
                            ? "text-rose-700 dark:text-rose-300"
                            : "text-emerald-700 dark:text-emerald-300",
                        )}
                      >
                        {currencySymbol} {money(netProfit)}
                      </p>
                      <p
                        className={cn(
                          "text-[10px] truncate",
                          netProfit < -0.009
                            ? "text-rose-600/90 dark:text-rose-400/90"
                            : "text-emerald-600/90 dark:text-emerald-400/90",
                        )}
                      >
                        {netProfit < -0.009
                          ? `${profitability.partnerDistributionCount} partner loss allocation`
                          : profitability.partnerDistributionCount > 0
                            ? `${profitability.partnerDistributionCount} partner profit distribution`
                            : "Net investor return"}
                      </p>
                    </div>

                    <div className="rounded-xl bg-background/80 border border-border/60 p-3 space-y-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Showroom Commission
                      </p>
                      <p className="text-base sm:text-lg font-extrabold text-foreground tabular-nums font-mono">
                        {currencySymbol} {money(profitability.showroomCommission)}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {profitability.showroomPercent > 0
                          ? `${profitability.showroomPercent.toFixed(1)}% deal retained`
                          : "Brokerage & retained margin"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="space-y-3 pt-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-bold text-base text-foreground tracking-tight">
                        Transactions Ledger
                      </h2>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground border border-border/40">
                        {filteredRows.length} records
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isSold
                        ? "Itemized ledger of vehicle acquisition, customs duties, customer sale realizations, and investor profit settlements."
                        : "Itemized ledger of capital advances, customs vouchers, auction settlements, and port disbursements."}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs font-semibold text-muted-foreground hover:text-foreground"
                      onClick={handleToggleExpandAll}
                    >
                      {allExpanded ? "Collapse All Details" : "Expand All Details"}
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5">
                  <div className="relative w-44 sm:w-52 shrink-0">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search ledger..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 pr-7 h-8 text-xs bg-background/60 border-border/70 rounded-lg shadow-2xs"
                    />
                    {search ? (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>

                  <div className="inline-flex rounded-lg border border-border/70 p-0.5 bg-muted/30">
                    <button
                      type="button"
                      onClick={() => setFlowFilter("all")}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-md transition-colors",
                        flowFilter === "all"
                          ? "bg-background text-foreground shadow-2xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      All Flows
                    </button>
                    <button
                      type="button"
                      onClick={() => setFlowFilter("receivables")}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-md inline-flex items-center gap-1 transition-colors",
                        flowFilter === "receivables"
                          ? "bg-background text-emerald-600 dark:text-emerald-400 shadow-2xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <ArrowDownLeft className="h-3 w-3" />
                      Receivables
                    </button>
                    <button
                      type="button"
                      onClick={() => setFlowFilter("payables")}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-md inline-flex items-center gap-1 transition-colors",
                        flowFilter === "payables"
                          ? "bg-background text-rose-600 dark:text-rose-400 shadow-2xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <ArrowUpRight className="h-3 w-3" />
                      Payables
                    </button>
                  </div>

                  <div className="inline-flex rounded-lg border border-border/70 p-0.5 bg-muted/30">
                    <button
                      type="button"
                      onClick={() => setStatusFilter("all")}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-md transition-colors",
                        statusFilter === "all"
                          ? "bg-background text-foreground shadow-2xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      All Status
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatusFilter("pending")}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-md transition-colors",
                        statusFilter === "pending"
                          ? "bg-background text-amber-600 dark:text-amber-400 shadow-2xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      Pending
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatusFilter("settled")}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-md transition-colors",
                        statusFilter === "settled"
                          ? "bg-background text-emerald-600 dark:text-emerald-400 shadow-2xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      Settled
                    </button>
                  </div>

                  <div className="flex-1 min-w-[12rem]">
                    <Select value={partyFilter} onValueChange={setPartyFilter}>
                      <SelectTrigger className="h-8 w-full text-xs bg-background/60 border-border/70 rounded-lg">
                        <SelectValue placeholder="All Parties" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Parties</SelectItem>
                        {uniqueParties.map((party) => {
                          const partyType =
                            transactions.find((row) => row.party === party)?.partyType || "";
                          return (
                            <SelectItem key={party} value={party}>
                              <span className="flex items-center gap-1.5">
                                <span>{party}</span>
                                {partyType && partyType !== "Party" ? (
                                  <span className="text-[10px] text-muted-foreground uppercase font-semibold">
                                    ({partyType})
                                  </span>
                                ) : null}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded-xl border border-border/70 bg-card/30 overflow-hidden shadow-xs">
                  <div className="overflow-x-auto">
                    <Table className="w-full min-w-[56rem] border-collapse">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-b border-border/60 bg-muted/30">
                          <TableHead className="w-[13%] whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                            DATE & REF
                          </TableHead>
                          <TableHead className="w-[38%] whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                            TRANSACTION & DESCRIPTION
                          </TableHead>
                          <TableHead className="w-[20%] whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                            PARTY
                          </TableHead>
                          <TableHead className="w-[16%] whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto">
                            AMOUNT
                          </TableHead>
                          <TableHead className="w-[13%] whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-muted-foreground p-2 h-auto pr-3">
                            STATUS
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="p-8 text-center text-sm text-muted-foreground">
                              No transactions match the selected filters.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRows.map((row) => {
                            const isExpanded = Boolean(expandedIds[row.id]);
                            const hasBreakdown = Boolean(row.breakdownItems?.length);
                            const requiredAmount = Number(row.requiredAmount ?? row.amount ?? 0);
                            const settledAmount = Number(
                              row.settledAmount ?? (row.status === "settled" ? requiredAmount : 0),
                            );
                            const dueAmount = Number(
                              row.dueAmount ?? Math.max(0, requiredAmount - settledAmount),
                            );
                            const excessAmount = Number(
                              row.excessAmount ?? Math.max(0, settledAmount - requiredAmount),
                            );
                            const displayStatus = displayStatusForRow(row);
                            const settledVerb = row.flow === "payable" ? "Paid" : "Received";

                            return (
                              <React.Fragment key={row.id}>
                                <TableRow className="border-b border-border/40 hover:bg-muted/15 transition-colors">
                                  <TableCell className="align-middle p-2">
                                    <p className="text-xs font-semibold text-foreground whitespace-nowrap">
                                      {row.date}
                                    </p>
                                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
                                      {row.refCode}
                                    </p>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="space-y-1 min-w-0">
                                        <p className="text-xs font-bold text-foreground">{row.title}</p>
                                        {row.description ? (
                                          <p className="text-[11px] text-muted-foreground line-clamp-2">
                                            {row.description}
                                          </p>
                                        ) : null}
                                        {(displayStatus === "Settled" || displayStatus === "Excess") &&
                                        row.settlementText ? (
                                          <p
                                            className="text-[10px] text-muted-foreground whitespace-nowrap font-medium flex items-center gap-1.5"
                                            style={{ marginTop: "0px" }}
                                          >
                                            <span
                                              className={cn(
                                                "h-1.5 w-1.5 rounded-full shrink-0",
                                                displayStatus === "Excess" ? "bg-amber-500" : "bg-emerald-500",
                                              )}
                                            />
                                            <span>{row.settlementText}</span>
                                          </p>
                                        ) : null}
                                      </div>
                                      {hasBreakdown ? (
                                        <button
                                          type="button"
                                          onClick={() => toggleExpand(row.id)}
                                          className="shrink-0 text-[11px] font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-0.5 ml-2"
                                        >
                                          {isExpanded ? (
                                            <>
                                              Hide <ChevronUp className="h-3 w-3" />
                                            </>
                                          ) : (
                                            <>
                                              Details <ChevronDown className="h-3 w-3" />
                                            </>
                                          )}
                                        </button>
                                      ) : null}
                                    </div>
                                  </TableCell>

                                  <TableCell className="align-middle p-2">
                                    <p className="text-xs font-semibold text-foreground">{row.party}</p>
                                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                      {row.partyType || "Party"}
                                    </p>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 text-right whitespace-nowrap">
                                    <div className="flex flex-col items-end gap-0.5">
                                      <p
                                        className={cn(
                                          "text-xs font-bold tabular-nums inline-flex items-center justify-end gap-1",
                                          row.flow === "payable"
                                            ? "text-rose-700 dark:text-rose-400"
                                            : "text-emerald-700 dark:text-emerald-400",
                                        )}
                                      >
                                        {row.flow === "payable" ? (
                                          <ArrowUpRight className="h-3 w-3 shrink-0" />
                                        ) : (
                                          <ArrowDownLeft className="h-3 w-3 shrink-0" />
                                        )}
                                        <span>
                                          {row.flow === "payable" ? "-" : "+"} {currencySymbol}{" "}
                                          {money(requiredAmount)}
                                        </span>
                                      </p>
                                      {(() => {
                                        if (excessAmount > 0.01) {
                                          return (
                                            <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400 tabular-nums text-right leading-snug">
                                              {settledVerb} {currencySymbol} {money(settledAmount)} · Excess {currencySymbol} {money(excessAmount)}
                                            </p>
                                          );
                                        }
                                        if (settledAmount > 0.01 && dueAmount > 0.01) {
                                          return (
                                            <p className="text-[10px] font-medium text-muted-foreground tabular-nums text-right leading-snug">
                                              <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{settledVerb} {currencySymbol} {money(settledAmount)}</span>
                                              {" · "}
                                              <span className="text-amber-700 dark:text-amber-400 font-semibold">Due {currencySymbol} {money(dueAmount)}</span>
                                            </p>
                                          );
                                        }
                                        if (settledAmount <= 0.01 && dueAmount > 0.01) {
                                          return (
                                            <p className="text-[10px] font-medium text-amber-700/80 dark:text-amber-400/80 tabular-nums text-right leading-snug">
                                              Pending full {row.flow === "payable" ? "payment" : "collection"}
                                            </p>
                                          );
                                        }
                                        if (settledAmount > 0.01 && dueAmount <= 0.01) {
                                          return (
                                            <p className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400 tabular-nums text-right leading-snug">
                                              Settled in full ✓
                                            </p>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  </TableCell>

                                  <TableCell className="align-middle p-2 text-right whitespace-nowrap pr-3">
                                    <div className="flex flex-col items-end">
                                      <span
                                        className={cn(
                                          "inline-flex items-center gap-1 text-[10px] font-bold",
                                          displayStatus === "Settled"
                                            ? "text-emerald-700 dark:text-emerald-400"
                                            : displayStatus === "Excess"
                                              ? "text-amber-700 dark:text-amber-400"
                                              : displayStatus === "Additional Due"
                                                ? "text-sky-700 dark:text-sky-400"
                                                : "text-amber-700 dark:text-amber-400",
                                        )}
                                      >
                                        <span
                                          className={cn(
                                            "h-1.5 w-1.5 rounded-full",
                                            displayStatus === "Settled"
                                              ? "bg-emerald-500"
                                              : displayStatus === "Excess"
                                                ? "bg-amber-500"
                                                : displayStatus === "Additional Due"
                                                  ? "bg-sky-500"
                                                  : "bg-amber-500",
                                          )}
                                        />
                                        {displayStatus}
                                      </span>
                                    </div>
                                  </TableCell>
                                </TableRow>

                                {isExpanded && hasBreakdown ? (
                                  <TableRow className="border-b border-border/40 bg-muted/15 hover:bg-muted/15">
                                    <TableCell colSpan={5} className="p-3 pl-6 sm:pl-6 pr-6">
                                      <div className="rounded-xl border border-border/70 bg-card/90 p-4 space-y-2.5 shadow-xs">
                                        <div className="flex items-center justify-between border-b border-border/40 pb-2">
                                          <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                                            <FileText className="h-3.5 w-3.5 text-primary" />
                                            {row.breakdownKind === "payments"
                                              ? `ITEMIZED PAYMENT BREAKDOWN (${row.title.toUpperCase()})`
                                              : `ITEMIZED COST & CUSTOMS BREAKDOWN (${row.title.toUpperCase()})`}
                                          </div>
                                          <span className="text-[10px] font-mono font-medium text-muted-foreground tracking-wider">
                                            REF: {row.refCode}
                                          </span>
                                        </div>

                                        <div className="space-y-1.5 divide-y divide-border/20 text-xs">
                                          {row.breakdownItems.map((item, idx) => (
                                            <div
                                              key={`${row.id}-bd-${idx}`}
                                              className="flex items-center justify-between pt-1.5 text-foreground/90"
                                            >
                                              <div className="space-x-1.5 min-w-0 pr-3">
                                                <span className="font-semibold text-foreground">
                                                  {item.title}
                                                </span>
                                                {item.subtitle ? (
                                                  <span className="text-[11px] text-muted-foreground">
                                                    {item.subtitle}
                                                  </span>
                                                ) : null}
                                              </div>
                                              <span className="font-mono font-bold tabular-nums text-foreground shrink-0">
                                                {currencySymbol} {money(item.amount)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>

                                        <div className="border-t border-border/50 pt-2 flex items-center justify-between text-xs font-bold">
                                          <span className="text-muted-foreground">
                                            Total Line Items: {row.breakdownItems.length}
                                          </span>
                                          <span className="text-foreground tabular-nums">
                                            {(() => {
                                              const paymentItems = row.breakdownItems.filter(
                                                (item) => item.kind === "payment",
                                              );
                                              const isPaymentBreakdown =
                                                row.breakdownKind === "payments" || paymentItems.length > 0;
                                              if (isPaymentBreakdown) {
                                                const receivedTotal =
                                                  (paymentItems.length > 0
                                                    ? paymentItems
                                                    : row.breakdownItems
                                                  ).reduce((sum, item) => sum + Number(item.amount || 0), 0);
                                                return `${
                                                  row.flow === "payable" ? "Total Paid" : "Total Received"
                                                }: ${currencySymbol} ${money(receivedTotal)}`;
                                              }
                                              return `Total Obligation: ${currencySymbol} ${money(row.amount)}`;
                                            })()}
                                          </span>
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
